import { useCallback, useState } from 'react';
import { Link, Navigate, useParams } from 'react-router';
import { useQuery } from '@tanstack/react-query';

import { ROUTES } from '#/config/routes';
import { fetchWorkOrderStockTransferDetail } from '#/api/momMgmt/workOrder/transfer/fetchDetail';
import { Button } from '#/modules/commonComponents/button';
import PageShell from '#/modules/infra/layout/PageShell';
import { fetchWarehouseSelectOptions } from '#/modules/imsMgmt/settings/storePlace/helper/warehouseSelectOptions';
import WorkOrderStockOpsPageHeader from '#/modules/momMgmt/workOrder/components/WorkOrderStockOpsPageHeader';
import { getWorkOrderApiErrorMessage } from '#/modules/momMgmt/workOrder/helper/apiError';
import FormCacheStatusBanner from '#/modules/momMgmt/workOrder/transfer/components/FormCacheStatusBanner';
import WorkOrderTransferShell from '#/modules/momMgmt/workOrder/transfer/components/WorkOrderTransferShell';
import {
  lookupQueryKey,
  workOrderStockTransferDetailQueryKey,
} from '#/utils/queryKeys';

const WorkOrderTransferPage = () => {
  const { id } = useParams();
  const isEdit = Boolean(id);
  const [formCacheBanner, setFormCacheBanner] = useState(null);

  const handleFormCacheStatusChange = useCallback((next) => {
    setFormCacheBanner(next);
  }, []);

  const warehouseQuery = useQuery({
    queryKey: lookupQueryKey('warehouseSelect'),
    queryFn: fetchWarehouseSelectOptions,
  });

  const detailQuery = useQuery({
    queryKey: workOrderStockTransferDetailQueryKey(id),
    queryFn: () => fetchWorkOrderStockTransferDetail(id),
    enabled: isEdit,
  });

  const warehouseOptions = warehouseQuery.data ?? [];
  const detail = detailQuery.data?.data;

  if (isEdit && detail && detail.status !== 'DRAFT') {
    return <Navigate to={ROUTES.workOrderTransferDetail(id)} replace />;
  }

  const isPageLoading = isEdit && detailQuery.isLoading;
  const isDetailError = isEdit && detailQuery.isError;
  const showShell = !isPageLoading && !isDetailError && (!isEdit || Boolean(detail));

  return (
    <PageShell variant="formTall">
      <div className="layout-shell__body">
        <div className="w-full">
          {isEdit ? (
            <div className="create_edit_page__header">
              <h1 className="page_title mb-0!">
                <span className="border-l-5 border-primary-400 pl-2.5">編輯工單轉倉</span>
              </h1>
              <div className="flex flex-wrap gap-2">
                <Button type="button" variant="outline" asChild>
                  <Link to={ROUTES.workOrderTransferDetail(id)}>返回詳情</Link>
                </Button>
                <Button type="button" variant="outline" asChild>
                  <Link to={ROUTES.workOrderTransfer}>返回列表</Link>
                </Button>
              </div>
            </div>
          ) : (
            <WorkOrderStockOpsPageHeader
              page="transfer"
              variant="form"
              statusSlot={formCacheBanner?.label ? (
                <FormCacheStatusBanner
                  status={formCacheBanner.status}
                  label={formCacheBanner.label}
                  actions={formCacheBanner.actions}
                />
              ) : null}
            />
          )}

          {isPageLoading ? (
            <p className="text-sm text-slate-500">載入中…</p>
          ) : null}

          {isDetailError ? (
            <p className="text-sm text-destructive">
              {getWorkOrderApiErrorMessage(detailQuery.error, '載入轉倉單失敗')}
            </p>
          ) : null}

          {showShell ? (
            <div className="sheet create_edit create_edit_form create_edit_form--page-scroll">
              <div className="create_edit_form__body pb-0!">
                <WorkOrderTransferShell
                  key={id ?? 'create'}
                  warehouseOptions={warehouseOptions}
                  isWarehouseLoading={warehouseQuery.isLoading}
                  transferId={id ?? null}
                  detail={isEdit ? detail : null}
                  onDetailRefetch={() => detailQuery.refetch()}
                  onFormCacheStatusChange={
                    isEdit ? undefined : handleFormCacheStatusChange
                  }
                />
              </div>
            </div>
          ) : null}
        </div>
      </div>
    </PageShell>
  );
};

export default WorkOrderTransferPage;
